

import React, { useRef } from 'react';
import { diffChars } from 'diff';   // character-level diff

// ── Inline styles (matches the old popup block look) ─────────────────────────

const BLOCK_STYLE = {
    border: '1px solid #d0d7de',
    borderRadius: '6px',
    background: '#ffffff',
    padding: '6px 10px',
    maxHeight: '70px',
    overflowY: 'auto',
    overflowX: 'auto',
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '12px',
    lineHeight: '1.6',
    wordBreak: 'break-all',
    whiteSpace: 'pre-wrap',
    color: '#24292f',
};

const LABEL_STYLE = {
    display: 'block',
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '4px',
    color: '#57606a',
};

const ARROW_STYLE = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px 0',
    color: '#57606a',
};

const REMOVED_SPAN = {
    background: '#ffc1c0',
    color: '#cf222e',
    borderRadius: '2px',
    padding: '0 1px',
};

const ADDED_SPAN = {
    background: '#acf2bc',
    color: '#1a7f37',
    borderRadius: '2px',
    padding: '0 1px',
};

// ── Component ─────────────────────────────────────────────────────────────────

export function PopupDiff({ oldText, newText }) {
    const origScrollRef = useRef(null);
    const suggScrollRef = useRef(null);

    const old_ = oldText ?? '';
    const new_ = newText ?? '';

    // Compute character-level diff
    // parts = [{ value, added?, removed? }, ...]
    const parts = diffChars(old_, new_);

    // Original box: unchanged + removed (red bg) — no added parts
    const origParts = parts.filter(p => !p.added);

    // Suggested box: unchanged + added (green bg) — no removed parts
    const suggParts = parts.filter(p => !p.removed);

    // Handle wheel scroll for each box (same as original popup)
    function attachWheel(ref) {
        return {
            onMouseEnter: () => {
                if (!ref.current) return;
                ref.current._wh = (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    ref.current.scrollTop += e.deltaY;
                };
                ref.current.addEventListener('wheel', ref.current._wh, { passive: false });
            },
            onMouseLeave: () => {
                if (!ref.current?._wh) return;
                ref.current.removeEventListener('wheel', ref.current._wh);
                ref.current._wh = null;
            },
        };
    }

    return (
        <div style={{ padding: '0 10px 8px', maxHeight: '500px' }}>

            {/* ── Original box ─────────────────────────────────────────────── */}
            <div>
                <span style={LABEL_STYLE}>Original</span>
                <div
                    ref={origScrollRef}
                    style={BLOCK_STYLE}
                    {...attachWheel(origScrollRef)}
                >
                    {origParts.map((part, i) => (
                        <span
                            key={i}
                            style={part.removed ? REMOVED_SPAN : undefined}
                        >
                            {part.value}
                        </span>
                    ))}
                </div>
            </div>

            {/* ── Arrow ────────────────────────────────────────────────────── */}
            <div style={ARROW_STYLE}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <polyline points="19 12 12 19 5 12" />
                </svg>
            </div>

            {/* ── Suggested box ────────────────────────────────────────────── */}
            <div>
                <span style={LABEL_STYLE}>Suggested</span>
                <div
                    ref={suggScrollRef}
                    style={{
                        ...BLOCK_STYLE,
                        background: new_ === '' ? '#fff8f8' : '#f6fff8',
                        borderColor: new_ === '' ? '#ffc1c0' : '#acf2bc',
                    }}
                    {...attachWheel(suggScrollRef)}
                >
                    {new_ === '' ? (
                        <span style={{ color: '#cf222e', fontStyle: 'italic' }}>
                            this content will be deleted
                        </span>
                    ) : (
                        suggParts.map((part, i) => (
                            <span
                                key={i}
                                style={part.added ? ADDED_SPAN : undefined}
                            >
                                {part.value}
                            </span>
                        ))
                    )}
                </div>
            </div>

        </div>
    );
}